import { Plus, Minus } from "lucide-react";
import { motion } from "motion/react";
import { useState } from "react";

import { useReducedMotion } from "./ui/use-reduced-motion";

type FAQProps = {
  faqs: {
    question: string;
    answer: React.ReactNode;
  }[];
  /** Pages about one narrow topic override these so the section stays on-topic. */
  title?: string;
  subtitle?: string;
  /** "compact" matches the MCP pages' 20px intro scale; default is the landing's. */
  subtitleScale?: "default" | "compact";
};

export default function FAQ({
  faqs,
  title = "FAQs",
  subtitle = "Everything you need to know about Flowstep",
  subtitleScale = "default",
}: FAQProps) {
  const prefersReducedMotion = useReducedMotion();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const preventBackgroundClickEffect = (e: React.MouseEvent) => {
    // e.preventDefault();
    e.stopPropagation();
  };

  return (
    <section id="faq" className="relative w-full py-16 lg:py-28">
      <div className="relative mx-auto max-w-3xl px-4 md:px-8 lg:px-16">
        {/* Header */}
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={
            prefersReducedMotion ? { duration: 0 } : { duration: 0.6 }
          }
          className="mb-8 text-center md:mb-16"
        >
          <h2
            className="mb-6 leading-[0.95] tracking-tight text-gray-900 dark:text-white"
            style={{
              fontSize: "clamp(2.25rem, 4.5vw, 4rem)",
              fontWeight: "600",
            }}
          >
            {title}
          </h2>
          <p
            className="mx-auto max-w-2xl leading-relaxed text-gray-600 dark:text-gray-300"
            style={{
              fontSize:
                subtitleScale === "compact"
                  ? "clamp(1.125rem, 2vw, 1.25rem)"
                  : "clamp(1.125rem, 2.5vw, 1.5rem)",
            }}
          >
            {subtitle}
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div
          className="space-y-2"
          onClick={preventBackgroundClickEffect}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;

            return (
              <motion.div
                key={index}
                initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : { duration: 0.5, delay: index * 0.05 }
                }
              >
                <button
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                  onMouseEnter={() => setHoveredIndex(index)}
                  className="group relative w-full cursor-pointer overflow-hidden text-left"
                >
                  {/* Sliding hover highlight — one pill glides between rows */}
                  {hoveredIndex === index && (
                    <motion.span
                      aria-hidden
                      layoutId="faq-hover"
                      transition={
                        prefersReducedMotion
                          ? { duration: 0 }
                          : { type: "spring", bounce: 0.2, duration: 0.4 }
                      }
                      className="absolute inset-0 rounded-lg bg-gray-50/50 dark:bg-white/5"
                    />
                  )}
                  {/* Gradient accent bar - appears on hover or when open */}
                  <motion.div
                    className="absolute top-0 bottom-0 left-0 w-1.5 bg-gradient-to-b from-blue-500 to-purple-500 md:w-1"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: isOpen ? 1 : 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                  />

                  <div className="relative px-4 py-6 md:px-8">
                    <div className="flex items-start justify-between gap-4 md:gap-6">
                      <h3
                        className="font-medium text-gray-900 transition-colors duration-200 group-hover:text-gray-950 dark:text-white dark:group-hover:text-white"
                        style={{ fontSize: "clamp(1.125rem, 2.2vw, 1.25rem)" }}
                      >
                        {faq.question}
                      </h3>
                      <motion.div
                        animate={{ rotate: isOpen ? 0 : 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-1 shrink-0"
                      >
                        {isOpen ? (
                          <Minus className="h-6 w-6 text-blue-600 md:h-5 md:w-5" />
                        ) : (
                          <Plus className="h-6 w-6 text-gray-400 transition-colors group-hover:text-gray-600 md:h-5 md:w-5 dark:text-gray-500 dark:group-hover:text-gray-300" />
                        )}
                      </motion.div>
                    </div>

                    <motion.div
                      initial={false}
                      animate={{
                        height: isOpen ? "auto" : 0,
                        opacity: isOpen ? 1 : 0,
                      }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div
                        className="pt-4 pr-8 leading-relaxed text-gray-600 md:pr-11 dark:text-gray-300"
                        style={{ fontSize: "clamp(1rem, 2vw, 1.125rem)" }}
                      >
                        {faq.answer}
                      </div>
                    </motion.div>
                  </div>
                </button>

                {/* Subtle divider */}
                {index < faqs.length - 1 && (
                  <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent dark:via-white/15" />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
